'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useEffect, useContext, useRef, Children, createContext } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import Link from 'next/link';
import Typography from '../../Base/Typography/Typography.js';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import CountBadge from '../../DataDisplays/CountBadge/CountBadge.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const TextTabContext = /*#__PURE__*/ createContext({
    barColor: 'bg-w-gray-900',
    activeTab: 0,
    currentActiveTab: 0,
    isFit: true,
    hasPadding: false,
    isFull: false,
    handleTabClick: (index)=>{}
});
/**
 * TextTab의 Root 컴포넌트입니다.
 * TextTab의 상태를 관리합니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 자식 컴포넌트
 * @param {number} [props.currentActiveTab=0] - 현재 활성화된 탭의 인덱스
 * @param {boolean} [props.isFit=true] - 탭의 너비를 컨텐츠에 맞출지 여부
 * @param {boolean} [props.hasPadding=false] - 좌우 패딩 적용 여부
 * @param {boolean} [props.isFull=false] - 탭이 전체 너비를 차지할지 여부
 * @param {string} [props.barColor='bg-w-gray-900'] - 선택 표시줄의 색상
 * @param {(index: number) => void} [props.onChange] - 탭 변경 시 호출되는 콜백 함수
 *
 * @example
 * <TextTab.Root currentActiveTab={0} onChange={(index) => console.log(index)}>
 *   <TextTab.Tabs>
 *     <TextTab.Tab index={0}>첫 번째 탭</TextTab.Tab>
 *     <TextTab.Tab index={1}>두 번째 탭</TextTab.Tab>
 *   </TextTab.Tabs>
 *   <TextTab.Panel index={0}>첫 번째 패널 내용</TextTab.Panel>
 *   <TextTab.Panel index={1}>두 번째 패널 내용</TextTab.Panel>
 * </TextTab.Root>
 */ const Root = ({ children, currentActiveTab = 0, isFit = true, hasPadding = false, isFull = false, barColor = 'bg-w-gray-900', onChange })=>{
    const [activeTab, setActiveTab] = useState(currentActiveTab);
    const handleTabClick = (index)=>{
        if (index === activeTab) return;
        onChange === null || onChange === void 0 ? void 0 : onChange(index);
        setActiveTab(index);
    };
    useEffect(()=>{
        if (currentActiveTab !== activeTab) {
            setActiveTab(currentActiveTab);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentActiveTab
    ]);
    return /*#__PURE__*/ jsx(TextTabContext.Provider, {
        value: {
            barColor,
            activeTab,
            currentActiveTab,
            isFit,
            hasPadding,
            handleTabClick,
            isFull
        },
        children: children
    });
};
/**
 * 탭 목록을 감싸는 컨테이너 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 탭 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * <TextTab.Tabs>
 *   <TextTab.Tab index={0}>첫 번째 탭</TextTab.Tab>
 *   <TextTab.Tab index={1}>두 번째 탭</TextTab.Tab>
 * </TextTab.Tabs>
 */ const Tabs = ({ children, className })=>{
    const { activeTab, hasPadding, isFull, barColor } = useContext(TextTabContext);
    const tabRefs = useRef([]);
    const underbarRef = useRef(null);
    const containerRef = useRef(null);
    const childrenArray = Children.toArray(children);
    const [isScrolling, setIsScrolling] = useState(false);
    // 터치 이벤트 핸들러
    const handleTouchStart = ()=>{
        setIsScrolling(true);
    };
    const handleTouchEnd = ()=>{
        setIsScrolling(false);
    };
    useEffect(()=>{
        if (!tabRefs.current[activeTab] || !underbarRef.current || isScrolling) return;
        const activeTabElement = tabRefs.current[activeTab];
        const underbarElement = underbarRef.current;
        const containerElement = containerRef.current;
        if (activeTabElement && underbarElement && containerElement) {
            const { offsetLeft, offsetWidth } = activeTabElement;
            const { scrollLeft, clientWidth } = containerElement;
            underbarElement.style.width = `${offsetWidth}px`;
            underbarElement.style.transform = `translateX(${offsetLeft}px)`;
            const isVisible = scrollLeft <= offsetLeft && scrollLeft + clientWidth >= offsetLeft + offsetWidth;
            if (!isVisible) {
                const scrollTimeout = setTimeout(()=>{
                    activeTabElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'center'
                    });
                }, 0);
                return ()=>clearTimeout(scrollTimeout);
            }
        }
    }, [
        activeTab,
        isScrolling
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        ref: containerRef,
        className: twJoin('relative block w-full touch-pan-x items-start overflow-x-scroll border-b border-w-gray-200 scrollbar-hide', hasPadding && 'px-9', className),
        "data-testid": "design-system-textTab--container",
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
        children: [
            /*#__PURE__*/ jsx(Box, {
                className: twMerge('inline-flex w-max gap-10', isFull && 'w-full gap-0'),
                children: childrenArray.map((child, index)=>{
                    return /*#__PURE__*/ jsx(Box, {
                        className: twMerge(isFull && 'w-full'),
                        ref: (el)=>{
                            tabRefs.current[index] = el;
                        },
                        children: child
                    }, index);
                })
            }),
            /*#__PURE__*/ jsx("span", {
                ref: underbarRef,
                "data-testid": `design-system-textTab--underbar-${activeTab}`,
                className: twJoin('absolute bottom-0 left-0 h-0.5 rounded-t-lg transition-all duration-300 ease-out', barColor)
            })
        ]
    });
};
/**
 * 개별 탭 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 탭의 레이블
 * @param {number} props.index - 탭의 인덱스
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {string} [props.barColor] - 선택 표시줄의 색상
 * @param {boolean} [props.disabled=false] - 탭 비활성화 여부
 * @param {number} [props.badgeNumber] - 뱃지에 표시될 숫자
 * @param {string} [props.iconName] - 탭에 표시될 아이콘 이름
 *
 * @example
 * <TextTab.Tab
 *   index={0}
 *   iconName="home"
 *   badgeNumber={5}
 * >
 *   홈
 * </TextTab.Tab>
 */ const Tab = ({ children, index, className, disabled = false, badgeNumber, iconName, href })=>{
    const { isFit, handleTabClick, activeTab, isFull } = useContext(TextTabContext);
    const isSelected = index === activeTab;
    const sharedProps = {
        tabIndex: 0,
        'data-testid': `design-system-textTab--item-${index}`,
        className: twMerge('group flex w-max cursor-pointer items-center rounded-xl hover:font-medium', disabled && 'cursor-not-allowed', isFull && `w-full`, className),
        onClick: ()=>{
            !disabled && handleTabClick(index);
        }
    };
    const content = /*#__PURE__*/ jsxs(Box, {
        className: twMerge('relative flex w-full flex-row items-center justify-center gap-2 py-4 font-normal text-w-gray-600 group-hover:text-w-gray-900', !isFit && 'px-[33.5px]', isFull && 'w-full px-0', isSelected && 'font-medium text-w-gray-900'),
        children: [
            iconName && /*#__PURE__*/ jsx(Box, {
                className: "flex size-4 flex-shrink-0 items-center justify-center",
                children: /*#__PURE__*/ jsx(SystemIcon, {
                    name: iconName,
                    className: twMerge('size-4 text-w-gray-600 group-hover:text-w-gray-900', isSelected && 'text-w-gray-900')
                })
            }),
            /*#__PURE__*/ jsx(Typography, {
                variant: "body1",
                className: "relative",
                children: children
            }),
            badgeNumber !== undefined && /*#__PURE__*/ jsx(CountBadge, {
                text: badgeNumber,
                showZero: true,
                className: "relative",
                variant: "gray"
            })
        ]
    });
    if (href) {
        return /*#__PURE__*/ jsx(Link, _object_spread_props(_object_spread({
            href: href
        }, sharedProps), {
            children: content
        }));
    }
    return /*#__PURE__*/ jsx(Box, _object_spread_props(_object_spread({}, sharedProps), {
        children: content
    }));
};
/**
 * 탭 패널 컴포넌트입니다.
 * 해당 탭이 선택되었을 때만 내용을 표시합니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 패널의 내용
 * @param {number} props.index - 패널의 인덱스 (연결된 탭의 인덱스와 일치해야 함)
 *
 * @example
 * <TextTab.Panel index={0}>
 *   첫 번째 탭의 내용입니다.
 * </TextTab.Panel>
 */ const Panel = ({ children, index })=>{
    const { activeTab } = useContext(TextTabContext);
    return activeTab === index && /*#__PURE__*/ jsx(Box, {
        "data-testid": "design-system-textTab--content",
        children: children
    });
};

export { Panel, Root, Tab, Tabs };
